import React, { useRef } from 'react';
import { TicketPlus, ImagePlus, X, Paperclip } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { toast } from '@/utils/safeToast';
import { fileToCompressedDataUrl, MAX_SCREENSHOTS } from './coryDiagnostics';

export const CoryTicketForm = ({
  ticketData, setTicketData, loading, onSubmit, onCancel,
  screenshots = [], setScreenshots,
}) => {
  const fileRef = useRef(null);

  const handleFiles = async (e) => {
    const files = Array.from(e.target.files || []);
    e.target.value = '';
    if (!files.length) return;
    const room = MAX_SCREENSHOTS - screenshots.length;
    if (room <= 0) {
      toast.error(`You can attach up to ${MAX_SCREENSHOTS} screenshots.`);
      return;
    }
    try {
      const encoded = await Promise.all(files.slice(0, room).map(fileToCompressedDataUrl));
      setScreenshots((prev) => [...prev, ...encoded]);
    } catch {
      toast.error('Could not attach that file — please use an image (PNG/JPG).');
    }
  };

  const removeShot = (idx) => setScreenshots((prev) => prev.filter((_, i) => i !== idx));

  return (
    <div
      className="px-4 py-3 border-t border-slate-200/60 bg-white/60 space-y-2 shrink-0"
      data-testid="cory-ticket-form"
    >
      <div className="flex items-center justify-between">
        <p className="text-xs font-medium text-slate-700 flex items-center gap-1.5">
          <TicketPlus className="w-3.5 h-3.5 text-indigo-500" />
          Create Support Ticket
        </p>
        <button onClick={onCancel} className="text-xs text-slate-400 hover:text-slate-700" data-testid="cory-ticket-cancel">
          Cancel
        </button>
      </div>
      <input
        data-testid="cory-ticket-subject"
        value={ticketData.subject}
        onChange={(e) => setTicketData((p) => ({ ...p, subject: e.target.value }))}
        placeholder="Subject"
        className="w-full text-xs px-3 py-2 rounded-xl border border-slate-200 bg-white focus:outline-none focus:ring-2 focus:ring-indigo-200 focus:border-indigo-300 transition-all"
      />
      <textarea
        data-testid="cory-ticket-description"
        value={ticketData.description}
        onChange={(e) => setTicketData((p) => ({ ...p, description: e.target.value }))}
        placeholder="Describe the issue — what were you doing when it happened?"
        rows={2}
        className="w-full text-xs px-3 py-2 rounded-xl border border-slate-200 bg-white resize-none focus:outline-none focus:ring-2 focus:ring-indigo-200 focus:border-indigo-300 transition-all"
      />

      {/* Screenshot attachments */}
      {screenshots.length > 0 && (
        <div className="flex flex-wrap gap-2" data-testid="cory-ticket-thumbs">
          {screenshots.map((src, i) => (
            <div key={i} className="relative w-12 h-12 rounded-lg overflow-hidden border border-slate-200">
              <img src={src} alt={`screenshot ${i + 1}`} className="w-full h-full object-cover" />
              <button
                type="button"
                onClick={() => removeShot(i)}
                data-testid={`cory-ticket-remove-shot-${i}`}
                className="absolute top-0 right-0 bg-black/60 text-white rounded-bl-md p-0.5 hover:bg-black/80"
                title="Remove"
              >
                <X className="w-3 h-3" />
              </button>
            </div>
          ))}
        </div>
      )}

      <div className="flex items-center gap-2">
        <input
          ref={fileRef}
          type="file"
          accept="image/*"
          multiple
          onChange={handleFiles}
          className="hidden"
          data-testid="cory-ticket-file-input"
        />
        <button
          type="button"
          onClick={() => fileRef.current?.click()}
          data-testid="cory-ticket-attach"
          disabled={screenshots.length >= MAX_SCREENSHOTS}
          className="flex items-center gap-1 text-[11px] px-2 py-1.5 rounded-xl border border-slate-200 bg-white text-slate-600 hover:text-indigo-600 hover:border-indigo-200 transition-colors disabled:opacity-40"
          title="Attach a screenshot"
        >
          <ImagePlus className="w-3.5 h-3.5" />
          Attach
        </button>
        <select
          data-testid="cory-ticket-priority"
          value={ticketData.priority}
          onChange={(e) => setTicketData((p) => ({ ...p, priority: e.target.value }))}
          className="text-xs px-2 py-1.5 rounded-xl border border-slate-200 bg-white"
        >
          <option value="standard">Standard (Free)</option>
          <option value="priority">Priority ($149 — 7hr SLA)</option>
        </select>
        <Button
          data-testid="cory-ticket-submit"
          size="sm"
          onClick={onSubmit}
          disabled={loading || !ticketData.subject || !ticketData.description}
          className="ml-auto text-xs h-7 rounded-xl bg-indigo-600 hover:bg-indigo-700 text-white shadow-sm"
        >
          Submit
        </Button>
      </div>
      {screenshots.length > 0 && (
        <p className="text-[10px] text-slate-400 flex items-center gap-1">
          <Paperclip className="w-3 h-3" />
          {screenshots.length} screenshot{screenshots.length > 1 ? 's' : ''} + device diagnostics will be sent to support.
        </p>
      )}
    </div>
  );
};
